'use client';

import { useEffect, useRef } from 'react';

/**
 * Hace que el botón "Atrás" del navegador cierre un drawer/modal en vez de
 * sacar al usuario del sitio.
 *
 * En el celular, el gesto natural para salir de un panel que tapa la pantalla
 * es "Atrás". Sin esto, el drawer de filtros se abría sobre el listado y ese
 * gesto abandonaba la página entera (o el sitio, si era la primera).
 *
 * Cómo funciona:
 *  - Al ABRIR, se empuja una entrada al historial con la misma URL. Next.js
 *    parchea `pushState` y le copia su estado interno (`__NA` + árbol), así que
 *    la entrada nueva es "suya": volver a ella no recarga la página.
 *  - Si el usuario toca Atrás, llega `popstate`: se consume nuestra entrada y
 *    se llama a `cerrar`.
 *  - Si se cierra por otra vía (X, "Aplicar", el fondo, arrastrar el drawer),
 *    la limpieza del efecto hace `history.back()` para que NO quede una entrada
 *    de más: cerrar y volver a abrir diez veces no deja diez pasos de historial.
 *
 * Al desmontar el componente con el panel abierto (por ejemplo, una navegación
 * a otra página) NO se hace `back()`: eso desharía la navegación nueva. En ese
 * caso puede quedar una entrada repetida de la URL del listado, que es
 * inofensiva.
 *
 * La URL que escribe `useVehicleFiltersUrl` con `replaceState` mientras el
 * panel está abierto vive en NUESTRA entrada; al volver atrás la URL vuelve a
 * la de antes de abrir, y el efecto de escritura de ese hook la recompone al
 * instante a partir del store. Las selecciones no se pierden.
 */
export function useCerrarConAtras(abierto: boolean, cerrar: () => void): void {
  const cerrarRef = useRef(cerrar);
  cerrarRef.current = cerrar;

  /** Hay una entrada nuestra en lo alto del historial. */
  const entradaActiva = useRef(false);
  /** El componente sigue montado (ver el bloque de desmontaje más abajo). */
  const montado = useRef(true);

  // Va ANTES del efecto principal: React corre las limpiezas en el orden en que
  // se declararon, así que al desmontar este flag ya está en false cuando el
  // efecto de abajo decide si hace `back()` o no.
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (!abierto || typeof window === 'undefined') return;

    window.history.pushState({ cerrarConAtras: true }, '');
    entradaActiva.current = true;

    const onPopState = () => {
      if (!entradaActiva.current) return;
      entradaActiva.current = false;
      cerrarRef.current();
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      if (entradaActiva.current && montado.current) {
        // Se cerró sin pasar por Atrás: se consume la entrada que empujamos.
        entradaActiva.current = false;
        window.history.back();
      }
      entradaActiva.current = false;
    };
  }, [abierto]);
}

export default useCerrarConAtras;
